import { VStack } from '@chakra-ui/react';
import type { Dispatch } from 'react';
import type { SelectOption, EditorProps } from '@kula-ai/components';
import { EmailBlockSection } from '../../../components/EmailBlockSection';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import { IcsAttachmentCheckbox } from '../../../components/IcsAttachmentCheckbox';
import type {
  InterviewCommunicationAction,
  InterviewCommunicationFormState,
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../../../InterviewActivity/communication/types';
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/copy';
import {
  EmailSentBadge,
  type EmailSentBadgeProps,
} from '../../editMode/EmailSentBadge';

interface InterviewerPaneProps {
  state: InterviewCommunicationFormState;
  dispatch: Dispatch<InterviewCommunicationAction>;
  fieldErrors: InterviewCommunicationValidationErrors;
  /** Calendar-invite template options — `interviewer_notice`. */
  calendarTemplateOptions: SelectOption[];
  /** Email template options — `interviewer_interview_confirmation`. */
  emailTemplateOptions: SelectOption[];
  fromOptions: SelectOption[];
  /** Spinner state for the From Select while the delegatable-senders
   *  query resolves. */
  isFromOptionsLoading?: boolean;
  ccBccOptions: SelectOption[];
  calendarVariables: InterviewVariablesByCategory;
  emailVariables: InterviewVariablesByCategory;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
  sentEmailBadge?: EmailSentBadgeProps;
}

export const InterviewerPane = ({
  state,
  dispatch,
  fieldErrors,
  calendarTemplateOptions,
  emailTemplateOptions,
  fromOptions,
  isFromOptionsLoading = false,
  ccBccOptions,
  calendarVariables,
  emailVariables,
  uploadInlineImage,
  sentEmailBadge,
}: InterviewerPaneProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.communication;

  return (
    <VStack align="stretch" spacing="4" flex="1" minW={0}>
      <EmailBlockSection
        title={copy.interviewer.calendarTitle}
        sectionId="schedule-comm-interviewer-calendar"
      >
        <TemplatedEmail
          className="w-full"
          showFrom={false}
          showTo={false}
          showSubject={false}
          enableCcBcc={false}
          showTemplate
          showEditor
          editorVariant="calendarInvite"
          variables={calendarVariables}
          templateOptions={calendarTemplateOptions}
          applyTemplateBodyToEditor
          value={state.interviewerCalendar}
          onChange={(value) =>
            dispatch({ type: 'SET_INTERVIEWER_CALENDAR', payload: value })
          }
          errors={fieldErrors.interviewerCalendar}
          placeholders={{ template: copy.placeholders.template }}
        />
      </EmailBlockSection>

      <EmailBlockSection
        title={copy.interviewer.emailTitle}
        sectionId="schedule-comm-interviewer-email"
        showSwitch
        switchChecked={state.interviewerEmailEnabled}
        onSwitchChange={(checked) =>
          dispatch({ type: 'SET_INTERVIEWER_EMAIL_ENABLED', payload: checked })
        }
        suffix={
          sentEmailBadge ? <EmailSentBadge {...sentEmailBadge} /> : undefined
        }
      >
        {state.interviewerEmailEnabled ? (
          <>
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo={false}
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              variables={emailVariables}
              attachments={state.interviewerEmailAttachments}
              onAttachmentsChange={(items: InterviewEditorAttachmentItem[]) =>
                dispatch({
                  type: 'SET_INTERVIEWER_EMAIL_ATTACHMENTS',
                  payload: items,
                })
              }
              templateOptions={emailTemplateOptions}
              fromOptions={fromOptions}
              fromSelectProps={{ loading: isFromOptionsLoading }}
              ccBccOptions={ccBccOptions}
              applyTemplateBodyToEditor
              value={state.interviewerEmail}
              onChange={(value) =>
                dispatch({ type: 'SET_INTERVIEWER_EMAIL', payload: value })
              }
              errors={fieldErrors.interviewerEmail}
              placeholders={{
                from: copy.placeholders.from,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
            />
            <IcsAttachmentCheckbox
              checked={state.interviewerIcsEnabled}
              onChange={(v) =>
                dispatch({ type: 'SET_INTERVIEWER_ICS_ENABLED', payload: v })
              }
            />
          </>
        ) : null}
      </EmailBlockSection>
    </VStack>
  );
};
